import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { isTransient } from '../lib/errors';
import { ruDate } from '../lib/format';
import { nq } from '../lib/questions';
import { useToast } from './ErrorToast';
import { people as peopleText } from './PeoplePanel';
import { ConfirmDialog } from './ui';

export interface DraftToDelete {
  id: number;
  brigadeName: string;
  periodStart: string;
  periodEnd: string;
  people: number;
  /** Часов в черновике — строкой сервера (итог табеля); нет — не называются. */
  hours?: string | null;
  /**
   * Всех вопросов по табелю, без снятых (T117): в списке табелей — `questions` сервера, на странице табеля — список
   * вопросов; оба из одного вывода сервера, число одно.
   */
  questions?: number;
  /** Из них без ответа. */
  openQuestions?: number;
}

/**
 * Что пропадёт вместе с черновиком (003 FR-036, T117): «2 человека, 36,0 ч и 4 вопроса, из них без ответа 3», «…и 2
 * вопроса, все отвечены», «…, вопросов нет». Сервер без числа всех вопросов — «3 вопроса без ответа».
 */
function contents(d: DraftToDelete): string {
  const parts = [peopleText(d.people)];
  if (d.hours) parts.push(`${d.hours} ч`);
  const base = parts.join(', ');
  if (d.questions === undefined)
    return d.openQuestions ? `${base} и ${nq(d.openQuestions)} без ответа` : base;
  if (d.questions === 0) return `${base}, вопросов нет`;
  const open = d.openQuestions ?? 0;
  return `${base} и ${nq(d.questions)}, ${open > 0 ? `из них без ответа ${open}` : 'все отвечены'}`;
}

/**
 * Удаление черновика (FR-035; 003 FR-036) через подтверждение: вопрос с названием и периодом, последствия числами —
 * людей, часов, вопросов, — и что не изменится.
 * После удаления — тост; список табелей перезапрашивается. Табель убирается из кэша, если его экран закрыт:
 * открытый экран перезапросил бы удалённый табель (`404`) до перехода в список.
 */
export function DeleteDraftDialog({
  draft,
  onClose,
  onDeleted,
  screen,
}: {
  draft: DraftToDelete | null;
  onClose: () => void;
  onDeleted?: () => void;
  screen: string;
}) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const del = useMutation({
    mutationFn: (d: DraftToDelete) => api.deleteTs(d.id),
    onSuccess: (_, d) => {
      qc.removeQueries({ queryKey: ['timesheet', d.id], type: 'inactive' });
      void qc.invalidateQueries({ queryKey: ['timesheets'] });
      showInfo(`Черновик «${d.brigadeName}» удалён`);
      onClose();
      onDeleted?.();
    },
    onError: (e, d) =>
      showError(e, {
        screen,
        action: 'delete',
        lead: 'Черновик не удалён',
        retry: isTransient(e) ? () => del.mutate(d) : undefined,
      }),
  });

  return (
    <ConfirmDialog
      open={draft !== null}
      onClose={onClose}
      onConfirm={() => draft && del.mutate(draft)}
      busy={del.isPending}
      title={
        draft
          ? `Удалить черновик «${draft.brigadeName}» за ${ruDate(draft.periodStart).slice(0, 5)} — ${ruDate(draft.periodEnd).slice(0, 5)}?`
          : ''
      }
      description={
        draft && (
          <>
            В черновике {contents(draft)}. Табель исчезнет из списка вместе с правками часов и отметками «в
            обед». Проходы СКУД и другие табели не изменятся, действия останутся в журнале.
          </>
        )
      }
      confirmLabel="Удалить черновик"
    />
  );
}
